import type { CategoryDto } from '#Products/models/Pim/CategoryDto'

/**
 * Ancestry-derived Back toward Home (S51 / T-017).
 * Catalog: climb parentsIds → `/catalog` → `/`.
 * Non-catalog: history.back() if possible, else `/`.
 * Stateless — survives reload / deep link.
 */
export async function useMobileBack() {
  const route = useRoute()
  const router = useRouter()
  const { routing } = useProducts()
  const { byIds } = await useCategories()

  const isHome = computed(() => {
    if (route.path === '/' || route.path === '') return true
    return route.name === 'ProductsIndex'
  })

  /** Account hub owns its own ← Профиль chrome; hide global header Back there. */
  const isProfileSection = computed(
    () => route.path === '/profile' || route.path.startsWith('/profile/'),
  )

  const showBack = computed(() => !isHome.value && !isProfileSection.value)

  const catalogSlug = computed(() => {
    if (route.name !== 'ProductsCatalog') return undefined
    const param = route.params.category
    if (Array.isArray(param)) return param[0]
    return param as string | undefined
  })

  const currentCategory = computed<CategoryDto | undefined>(() => {
    const slug = catalogSlug.value
    if (!slug) return undefined
    const id = routing.extractIdFromSlug(slug)
    return byIds.value[id]
  })

  /** SPA target when Back can navigate by ancestry; null → history / home fallback */
  const ancestryTarget = computed<string | null>(() => {
    if (route.name === 'ProductsCatalog') {
      const category = currentCategory.value
      if (!category) {
        // Bare `/catalog` or unknown slug → Home
        return '/'
      }

      const parentId = category.parentsIds?.[0]
      if (parentId != null) {
        const parent = byIds.value[parentId]
        if (parent) {
          return routing.getCatalog(parent).route
        }
      }

      // Root category → catalog root
      return routing.getCatalog().route
    }

    return null
  })

  function goBack() {
    if (!showBack.value) return

    const target = ancestryTarget.value
    if (target != null) {
      navigateTo(target)
      return
    }

    if (import.meta.client && window.history.length > 1) {
      router.back()
      return
    }

    navigateTo('/')
  }

  return {
    isHome,
    showBack,
    goBack,
    ancestryTarget,
  }
}
